import React, { useEffect, useState } from 'react';
import logo from '../logo.png';
import './LiquidityList.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes, faSort, faSortUp, faSortDown, faSearch, faExternalLinkAlt, faSync } from '@fortawesome/free-solid-svg-icons';
import blockLinkUtils from '../utils/blocklink';
import { copyToClipboardWithToast } from '../utils/clipboard';
import { useNavigate, useSearchParams } from 'react-router';
import toast from 'react-hot-toast';
import enums from '../utils/enum';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import SingleSelectDropdown from '../components/SingleSelectDropdown';
import { ChainIcon } from '../components/ChainIcon';

interface Token {
    symbol: string;
    address: string;
    decimals: number;
    reserve: string;
    usd_price: number;
}

interface LiquidityPool {
    pool_id: string;
    chain: string;
    protocol: string;
    tvl_usd: number;
    day_usd_ratio: number;
    week_usd_ratio: number;
    fee_day_ratio: number;
    fee_week_ratio: number;
    tokens: Token[];
}

interface ApiResponse {
    code: number;
    message: string;
    data: {
        items: LiquidityPool[];
    };
}

type SortKey = 'tvl_usd' | 'day_usd_ratio' | 'week_usd_ratio' | 'fee_day_ratio' | 'fee_week_ratio';
type SortDirection = 'asc' | 'desc' | null;

interface SortConfig {
    key: SortKey | null;
    direction: SortDirection;
}

export function LiquidityList() {
    const [pools, setPools] = useState<LiquidityPool[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    // Derive state from URL search parameters
    const searchPoolId = searchParams.get('pool_id') || '';
    const backendSort = (searchParams.get('order_by') || 'tvl_usd') as SortKey;
    const chainsRaw = searchParams.get('chains');
    const protocolsRaw = searchParams.get('protocols');
    const selectedChains = chainsRaw ? chainsRaw.split(',') : [];
    const selectedProtocols = protocolsRaw ? protocolsRaw.split(',') : [];

    const [toolbarInput, setToolbarInput] = useState(searchPoolId);
    const [showFilter, setShowFilter] = useState(false); // This state is now unused due to global search
    const [inputPoolId, setInputPoolId] = useState(''); // This state is now unused due to global search
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Update toolbar input if URL changes (e.g. back navigation)
    useEffect(() => {
        setToolbarInput(searchPoolId);
    }, [searchPoolId]);

    const updateParams = (newParams: Record<string, string | null>) => {
        const nextParams = new URLSearchParams(searchParams);
        Object.entries(newParams).forEach(([key, value]) => {
            if (value === null || value === '') {
                nextParams.delete(key);
            } else {
                nextParams.set(key, value);
            }
        });
        setSearchParams(nextParams, { replace: true });
    };

    useEffect(() => {
        let url = `/web3_liquidity/api/v1/liquidity_index?order_by=${backendSort}`;
        if (searchPoolId) {
            url += `&pool_id=${searchPoolId}`;
        }
        if (selectedChains.length > 0) {
            url += `&chains=${selectedChains.join(',')}`;
        }
        if (selectedProtocols.length > 0) {
            url += `&protocols=${selectedProtocols.join(',')}`;
        }

        setLoading(true);
        fetch(url)
            .then(res => res.json())
            .then((data: ApiResponse) => {
                if (data.code === 0 && data.data && data.data.items) {
                    setPools(data.data.items);
                } else {
                    setPools([]);
                }
                setLoading(false);
            })
            .catch(err => {
                console.error('Failed to fetch pools', err);
                setPools([]);
                setLoading(false);
            });
    }, [backendSort, searchPoolId, chainsRaw, protocolsRaw, refreshTrigger]);

    // Sync toolbar input with external search changes (e.g. from clear button)
    useEffect(() => {
        setToolbarInput(searchPoolId);
    }, [searchPoolId]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(value);
    };

    const formatPercent = (value: number) => {
        if (value === 0) {
            return '-';
        }
        return (value * 100 - 100).toFixed(4) + '%';
    };

    const formatFeePercent = (value: number) => {
        return (value * 100).toFixed(4) + '%';
    };

    // These handlers are now unused due to global search
    const handleHeaderClick = () => {
        setShowFilter(!showFilter);
        setInputPoolId(searchPoolId); // Reset input to current active filter
    };

    const handleConfirm = () => {
        updateParams({ pool_id: inputPoolId });
        setShowFilter(false);
    };

    const handleCancel = (e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent bubbling just in case
        setShowFilter(false);
        // Optionally reset input to current search value if desired, but closing is enough as opening resets it.
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        updateParams({ pool_id: null });
        setInputPoolId('');
        setShowFilter(false);
    };

    const sortKey = searchParams.get('sort_key') as SortKey | null;
    const sortDir = searchParams.get('sort_dir') as SortDirection;

    const handleSort = (key: SortKey) => {
        let direction: SortDirection = 'desc';
        if (sortKey === key) {
            if (sortDir === 'desc') {
                direction = 'asc';
            } else if (sortDir === 'asc') {
                direction = null;
            }
        }
        updateParams({
            sort_key: direction ? key : null,
            sort_dir: direction
        });
    };

    const getSortIcon = (key: SortKey) => {
        if (sortKey !== key) return faSort;
        if (sortDir === 'asc') return faSortUp;
        return faSortDown;
    };

    const getYieldClass = (value: number) => {
        if (value > 1) return 'yield-positive';
        if (value < 1 && value > 0) return 'yield-negative';
        return '';
    };

    const handleToolbarSearch = (e: React.FormEvent) => {
        e.preventDefault();
        updateParams({ pool_id: toolbarInput });
    };

    const handleCopy = (text: string) => {
        copyToClipboardWithToast(text);
    };

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
        toast.success('Refreshing data...', {
            style: {
                background: '#333',
                color: '#fff',
            },
            iconTheme: {
                primary: '#6366f1',
                secondary: '#333',
            },
        });
    };

    const sortedPools = React.useMemo(() => {
        if ((!sortKey || !sortDir) && toolbarInput === '') {
            return pools;
        }
        return [...pools].filter((pool) => {
            return pool.pool_id.toLowerCase().includes(toolbarInput.toLowerCase()) || pool.tokens.some((token) => token.symbol.toLowerCase().includes(toolbarInput.toLowerCase()));
        }).sort((a, b) => {
            const aValue = a[sortKey!];
            const bValue = b[sortKey!];

            if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
    }, [pools, sortKey, sortDir, toolbarInput]);

    return (
        <div className="liquidity-list-page">
            <div className="landing-background">
                <div className="orb orb-1"></div>
                <div className="orb orb-2"></div>
                <div className="grid-overlay"></div>
            </div>
            <div className="list-header">
                <div className="list-header-logo-container" onClick={() => navigate('/')}>
                    <img src={logo} className="list-logo" alt="logo" />
                    <span className="list-app-name">LiquidMap</span>
                </div>
            </div>

            <div className="list-controls">
                <form className="search-box" onSubmit={handleToolbarSearch}>
                    <button type="submit" className="search-btn-icon">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    </button>
                    <input
                        type="text"
                        name="search"
                        placeholder="Search Liquidity Pool..."
                        value={toolbarInput}
                        onChange={(e) => setToolbarInput(e.target.value)}
                    />
                </form>
                <div className="controls-group">
                    <button className="refresh-btn" onClick={handleRefresh} title="Refresh Data">
                        <FontAwesomeIcon icon={faSync} spin={loading} />
                    </button>
                    <MultiSelectDropdown
                        label="Chain"
                        options={enums.chainProtocol.map(cp => cp.chain)}
                        selected={selectedChains}
                        onChange={(chains) => {
                            const params: Record<string, string | null> = { chains: chains.join(',') };

                            // Keep protocols that are still valid for the selected chains, or clear if no chains selected
                            if (chains.length === 0) {
                                params.protocols = null;
                            } else {
                                const validProtocols = new Set(
                                    enums.chainProtocol
                                        .filter(cp => chains.includes(cp.chain))
                                        .flatMap(cp => cp.protocol)
                                );
                                const filteredProtocols = selectedProtocols.filter(l => validProtocols.has(l));
                                params.protocols = filteredProtocols.length > 0 ? filteredProtocols.join(',') : null;
                            }
                            updateParams(params);
                        }}
                        placeholder="All Chains"
                    />
                    <MultiSelectDropdown
                        label="Protocol"
                        options={Array.from(new Set(
                            selectedChains.length > 0
                                ? enums.chainProtocol
                                    .filter(cp => selectedChains.includes(cp.chain))
                                    .flatMap(cp => cp.protocol)
                                : enums.chainProtocol.flatMap(cp => cp.protocol)
                        ))}
                        selected={selectedProtocols}
                        onChange={(protocols) => updateParams({ protocols: protocols.join(',') })}
                        placeholder="All Protocols"
                    />
                    <div className="sort-dropdown-container">
                        <SingleSelectDropdown
                            label="Sort by"
                            options={[
                                { label: 'TVL (USD)', value: 'tvl_usd' },
                                { label: '24H USD', value: 'day_usd_ratio' },
                                { label: '7D USD', value: 'week_usd_ratio' },
                                { label: '24H Fee', value: 'fee_day_ratio' },
                                { label: '7D Fee', value: 'fee_week_ratio' }
                            ]}
                            value={backendSort}
                            onChange={(value) => updateParams({ order_by: value })}
                        />
                    </div>
                </div>
            </div>

            <div className="table-container">
                {loading ? (
                    <div className="loading-container">Loading pools data...</div>
                ) : (
                    <table className="liquidity-table">
                        <thead>
                            <tr>
                                <th>Chain</th>
                                <th>
                                    <div className="th-content-wrapper">
                                        Pool ID
                                        {searchPoolId && (
                                            <div className="filter-badge-container" title={searchPoolId}>
                                                <span className="filter-badge">(Filtered)</span>
                                                <button className="header-clear-btn" onClick={handleClear} title="Clear Filter">
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            </div>
                                        )}
                                        {showFilter && (
                                            <div className="filter-popup" onClick={e => e.stopPropagation()}>
                                                <div className="filter-title">Filter by Pool ID</div>
                                                <input
                                                    className="filter-input"
                                                    value={inputPoolId}
                                                    onChange={e => setInputPoolId(e.target.value)}
                                                    placeholder="Enter Pool ID"
                                                />
                                                <div className="filter-actions">
                                                    <button className="filter-cancel-btn" onClick={handleCancel}>Cancel</button>
                                                    <button className="filter-confirm-btn" onClick={handleConfirm}>Confirm</button>
                                                </div>
                                            </div>
                                        )}
                                        <button className={`filter-icon-btn ${searchPoolId ? 'active' : ''} `} onClick={handleHeaderClick} title="Filter by Pool ID">
                                            <FontAwesomeIcon icon={faFilter} />
                                        </button>
                                    </div>
                                </th>
                                <th>Protocol</th>
                                <th>Tokens</th>
                                <th className="sortable-th" onClick={() => handleSort('tvl_usd')}>
                                    TVL (USD) <FontAwesomeIcon icon={getSortIcon('tvl_usd')} className={`sort-icon ${sortKey === 'tvl_usd' ? 'active' : ''}`} />
                                </th>
                                <th className="sortable-th" onClick={() => handleSort('day_usd_ratio')}>
                                    24-Hour USD <FontAwesomeIcon icon={getSortIcon('day_usd_ratio')} className={`sort-icon ${sortKey === 'day_usd_ratio' ? 'active' : ''}`} />
                                </th>
                                <th className="sortable-th" onClick={() => handleSort('week_usd_ratio')}>
                                    7-Day USD <FontAwesomeIcon icon={getSortIcon('week_usd_ratio')} className={`sort-icon ${sortKey === 'week_usd_ratio' ? 'active' : ''}`} />
                                </th>
                                <th className="sortable-th" onClick={() => handleSort('fee_day_ratio')}>
                                    24-Hour Fee <FontAwesomeIcon icon={getSortIcon('fee_day_ratio')} className={`sort-icon ${sortKey === 'fee_day_ratio' ? 'active' : ''}`} />
                                </th>
                                <th className="sortable-th" onClick={() => handleSort('fee_week_ratio')}>
                                    7-Day Fee <FontAwesomeIcon icon={getSortIcon('fee_week_ratio')} className={`sort-icon ${sortKey === 'fee_week_ratio' ? 'active' : ''}`} />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedPools.map((pool) => (
                                <tr
                                    key={`${pool.chain}_${pool.pool_id}`}
                                    onClick={() => {
                                        let url = '';
                                        switch (pool.protocol) {
                                            case 'UniswapV2':
                                            case 'PancakeSwapV2':
                                            case 'AerodromeV2':
                                                url = `/uniswap_v2/${pool.chain}/${pool.pool_id}`;
                                                break;
                                            case 'UniswapV3':
                                            case 'PancakeSwapV3':
                                            case 'AerodromeV3':
                                                url = `/uniswap_v3/${pool.chain}/${pool.pool_id}`;
                                                break;
                                            case 'UniswapV4':
                                            case 'PancakeInfCl':
                                                url = `/uniswap_v4/${pool.chain}/${pool.pool_id}`;
                                                break;
                                            case 'CurveStable':
                                                url = `/curve_stable/${pool.chain}/${pool.pool_id}`;
                                                break;
                                            default:
                                                break;
                                        }
                                        if (url) {
                                            window.open(url, '_blank');
                                        }
                                    }}
                                    className={['UniswapV2', 'PancakeSwapV2', 'UniswapV3', 'PancakeSwapV3', 'UniswapV4', 'PancakeInfCl', 'AerodromeV2', 'AerodromeV3', 'CurveStable'].includes(pool.protocol) ? 'clickable-row' : ''}
                                >
                                    <td><ChainIcon chain={pool.chain} /></td>
                                    <td>
                                        <div className="pool-id-container">
                                            <span
                                                className="address-copy-trigger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCopy(pool.pool_id);
                                                }}
                                                title="Click to copy address"
                                            >
                                                {pool.pool_id.substring(0, 6)}...{pool.pool_id.substring(pool.pool_id.length - 4)}
                                            </span>

                                            <a
                                                href={blockLinkUtils.getProtocolLink(pool.chain, pool.protocol, pool.pool_id)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => e.stopPropagation()}
                                                className="explorer-link"
                                                title="View on Block Explorer"
                                            >
                                                <FontAwesomeIcon icon={faExternalLinkAlt} />
                                            </a>
                                        </div>
                                    </td>
                                    <td className="col-protocol">{pool.protocol}</td>
                                    <td>
                                        <div className="token-icons">
                                            {pool.tokens && pool.tokens.map(t => (
                                                <div key={t.address} className="pool-id-container" style={{ display: 'inline-flex', marginRight: '8px' }}>
                                                    <span
                                                        className="token-link"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleCopy(t.address);
                                                        }}
                                                        title="Click to copy address"
                                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                    >
                                                        {t.symbol}
                                                    </span>
                                                    <a
                                                        href={blockLinkUtils.getAddressLink(pool.chain, t.address)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="explorer-link"
                                                        title="View on Block Explorer"
                                                        style={{ marginLeft: '4px' }}
                                                    >
                                                        <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
                                                    </a>
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td className="col-tvl">{formatCurrency(pool.tvl_usd)}</td>
                                    <td className={`col-yield ${getYieldClass(pool.day_usd_ratio)}`}>{formatPercent(pool.day_usd_ratio)}</td>
                                    <td className={`col-yield ${getYieldClass(pool.week_usd_ratio)}`}>{formatPercent(pool.week_usd_ratio)}</td>
                                    <td className={`col-yield ${getYieldClass(pool.fee_day_ratio + 1)}`}>{formatFeePercent(pool.fee_day_ratio)}</td>
                                    <td className={`col-yield ${getYieldClass(pool.fee_week_ratio + 1)}`}>{formatFeePercent(pool.fee_week_ratio)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default LiquidityList;
